import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiDollarSign, FiCreditCard, FiMoreHorizontal, FiSmartphone, FiCalendar, FiRepeat } from 'react-icons/fi';
import { BsBank, BsCashCoin, BsApple } from 'react-icons/bs';

// Payment method values stored by the backend → label + icon shown here
const METHODS = {
  Cash: { label: 'Cash', icon: BsCashCoin, color: '#10b981' },
  Card: { label: 'Card Swipe (Mada/Visa)', icon: FiCreditCard, color: '#6366f1' },
  Online: { label: 'Bank Transfer', icon: BsBank, color: '#3b82f6' },
  STCPay: { label: 'STC Pay', icon: FiSmartphone, color: '#7c3aed' },
  ApplePay: { label: 'Apple Pay', icon: BsApple, color: '#111827' },
  Tabby: { label: 'Tabby', icon: FiCalendar, color: '#0d9488' },
  Tamara: { label: 'Tamara', icon: FiRepeat, color: '#db2777' },
  UPI: { label: 'UPI', icon: FiDollarSign, color: '#f59e0b' },
  Other: { label: 'Other', icon: FiMoreHorizontal, color: '#64748b' },
};
const methodInfo = (m) => METHODS[m] || { label: m || '—', icon: FiDollarSign, color: '#64748b' };

const sar = (n) => `SAR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('en-GB', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';
// Today in Saudi time as YYYY-MM-DD (for the quick date buttons)
const saudiDay = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86400000);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
};

export default function SuperAdminPayments() {
  const navigate = useNavigate();
  // ?from=&to=&shopId= — set by the Dashboard's sales-total card
  const [params] = useSearchParams();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState(params.get('shopId') || '');
  const [startDate, setStartDate] = useState(params.get('from') || saudiDay(-6));
  const [endDate, setEndDate] = useState(params.get('to') || saudiDay(0));
  const [method, setMethod] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superAdminApi.get('/shops').then(r => setShops(r.data.shops)).catch(() => toast.error('Failed to load shops'));
  }, []);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminApi.get('/payments', {
        params: {
          shopId: shopId || undefined, startDate: startDate || undefined, endDate: endDate || undefined,
          method: method || undefined, receivedBy: receivedBy || undefined, page, limit: 50,
        },
      });
      setData(res.data);
    } catch { toast.error('Failed to load payments'); }
    setLoading(false);
  }, [shopId, startDate, endDate, method, receivedBy, page]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const setRange = (from, to) => { setStartDate(from); setEndDate(to); setPage(1); };
  const presets = [
    ['Today', saudiDay(0), saudiDay(0)],
    ['Yesterday', saudiDay(-1), saudiDay(-1)],
    ['7 days', saudiDay(-6), saudiDay(0)],
    ['30 days', saudiDay(-29), saudiDay(0)],
    ['All time', '', ''],
  ];

  return (
    <div>
      {/* ── Filters ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-control" style={{ width: 200 }} value={shopId} onChange={e => { setShopId(e.target.value); setPage(1); }}>
            <option value="">All Shops</option>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <input type="date" className="form-control" style={{ width: 155 }}
            value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" className="form-control" style={{ width: 155 }}
            value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} />
          <select className="form-control" style={{ width: 190 }} value={method} onChange={e => { setMethod(e.target.value); setPage(1); }}>
            <option value="">All payment types</option>
            {Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="form-control" style={{ width: 180 }} value={receivedBy} onChange={e => { setReceivedBy(e.target.value); setPage(1); }}>
            <option value="">Received by: anyone</option>
            {(data?.staff || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {presets.map(([label, from, to]) => {
              const active = startDate === from && endDate === to;
              return (
                <button key={label} className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setRange(from, to)}>{label}</button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Totals ── */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div className="card"><div className="card-body">
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>TOTAL RECEIVED</div>
            <div style={{ fontSize: 24, fontWeight: 800, marginTop: 4 }}>{sar(data.totalAmount)}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{data.total} payments</div>
          </div></div>
          {Object.entries(data.byMethod || {}).sort((a, b) => b[1] - a[1]).map(([m, amt]) => {
            const info = methodInfo(m); const Icon = info.icon;
            return (
              <div className="card" key={m}><div className="card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: info.color, fontWeight: 700 }}>
                  <Icon size={15} /> {info.label.toUpperCase()}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{sar(amt)}</div>
              </div></div>
            );
          })}
        </div>
      )}

      {data && Object.keys(data.byStaff || {}).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><strong style={{ fontSize: 14 }}>Received by staff</strong></div>
          <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {Object.entries(data.byStaff).sort((a, b) => b[1] - a[1]).map(([name, amt]) => (
              <div key={name} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{name === '—' ? 'Not recorded' : name}</div>
                <div style={{ fontSize: 13, color: '#10b981', fontWeight: 700 }}>{sar(amt)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Payment list ── */}
      <div className="card">
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Shop</th>
                  <th>Customer</th>
                  <th>Bill / Order</th>
                  <th>Amount</th>
                  <th>Payment type</th>
                  <th>Received by</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {(!data || data.payments.length === 0) && (
                  <tr><td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon">💰</div>
                      <h3>No payments in this period</h3>
                    </div>
                  </td></tr>
                )}
                {data?.payments.map(p => {
                  const info = methodInfo(p.method); const Icon = info.icon;
                  return (
                    <tr key={p._id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(p.date)}</td>
                      <td>{p.shop?.name || '—'}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{p.customerName || '—'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.customerPhone}</div>
                      </td>
                      <td>
                        {p.billId ? (
                          <span style={{ fontWeight: 700, color: '#3b82f6', cursor: 'pointer' }}
                            onClick={() => navigate(`/superadmin/sales/${p.billId}`)}>
                            #{p.billNumber}
                          </span>
                        ) : p.customerId ? (
                          <span style={{ color: '#3b82f6', cursor: 'pointer' }}
                            onClick={() => navigate(`/superadmin/customers/${p.customerId}`)}>
                            {p.orderLabel || 'Order'} (no bill)
                          </span>
                        ) : '—'}
                        {p.orderNumber && <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.orderNumber}</div>}
                      </td>
                      <td style={{ fontWeight: 700, color: p.amount < 0 ? '#ef4444' : '#10b981', whiteSpace: 'nowrap' }}>
                        {sar(p.amount)}
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: info.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          <Icon size={15} /> {info.label}
                        </span>
                      </td>
                      <td>{p.receivedBy || <span style={{ color: '#94a3b8' }}>Not recorded</span>}</td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {p.type}{p.note ? ` · ${p.note}` : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.pages > 1 && (
          <div style={{ padding: '12px 16px' }}>
            <div className="pagination">
              {Array.from({ length: data.pages }, (_, i) => (
                <button key={i} className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                  onClick={() => setPage(i + 1)}>{i + 1}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
